/*============================================================================
  Anniversary set module (cart drawer + /cart page), 2026-09-27.

  Visible cart layer for the "buy all three anniversary pieces, get the
  Perplex Anniversary Magazine" reward. Enforcement is NOT here: Shopify Flow
  adds the Magazine to qualifying orders after checkout. This module never
  writes a gift line and never touches pricing; its only cart write is the
  Add button on a missing-piece card, through theme.cartQueue.

  Gated by settings.anniv_set_module (layout/theme.liquid loads this file and
  snippets/cart-anniv-set-config.liquid renders #AnnivSetConfig only while it
  is on), independent of drawer_drop_mode and the GWP tier module.

  Mounts: every [data-anniv-set] element (drawer: snippets/cart-drawer.liquid,
  page: sections/main-cart.liquid). Both live OUTSIDE [data-products], so an
  AJAX line rebuild never wipes them; they repaint from a fresh cart.js read
  on cart:build, cart:updated and ajaxProduct:added.
  State logic: assets/cart-anniv-set-logic.js (unit-tested).
  Card markup + add path: theme.CartUpsellCard (assets/cart-drawer-modules.js),
  shared with the generic upsell rail so both keep one skeleton.
============================================================================*/
(function() {
  if (!window.theme || !theme.AnnivSetLogic || !theme.CartUpsellCard || !theme.readAnnivSetConfig) return;

  var config = theme.readAnnivSetConfig();
  if (!config || !config.enabled) return;

  var MAX_STALE_RETRIES = 2;
  var strings = config.strings || {};

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function AnnivSet(container) {
    this.container = container;
    this.isPage = container.getAttribute('data-anniv-set') === 'page';
    this.missing = [];
    this.index = 0;
    this.lastSignature = null;
  }

  AnnivSet.prototype = {
    // Re-reads the cart; if a queued cart write landed while the read was in
    // flight (theme.cartQueue stamp moved), reads again so the module never
    // paints a count older than the lines the drawer is showing (F-016 shape).
    refresh: function(retriesLeft) {
      retriesLeft = typeof retriesLeft === 'number' ? retriesLeft : MAX_STALE_RETRIES;
      var queue = theme.cartQueue;
      var startVersion = queue ? queue.stamp() : null;
      return theme.cart.getCart().then(function(cart) {
        if (startVersion !== null && queue && !queue.isCurrent(startVersion) && retriesLeft > 0) {
          return this.refresh(retriesLeft - 1);
        }
        var state = theme.AnnivSetLogic.computeSetState(cart.items || [], config.products);
        this.paint(state);
      }.bind(this)).catch(function(err) {
        if (theme.config && theme.config.debug) console.error(err);
      });
    },

    paint: function(state) {
      var soldOut = !!config.soldOut;
      // Skip identical repaints (cart:build and cart:updated often fire for
      // the same write) so the card fade doesn't replay and a size the
      // visitor just picked on a card isn't reset.
      var signature = [state.active, state.segments.join(','), soldOut].join('|');
      if (signature === this.lastSignature) return;
      this.lastSignature = signature;

      this.container.innerHTML = '';
      if (!state.active) {
        this.container.hidden = true;
        return;
      }
      this.container.hidden = false;

      var head = el('div', 'cart-drawer__anniv-set-head');
      head.appendChild(el('div', 'cart-drawer__anniv-set-title', strings.title));
      head.appendChild(el('div', 'cart-drawer__anniv-set-count',
        (strings.count || '').replace('[count]', state.heldCount).replace('[total]', state.total)));
      this.container.appendChild(head);

      var segments = el('div', 'cart-drawer__anniv-set-segments');
      segments.setAttribute('role', 'img');
      segments.setAttribute('aria-label',
        (strings.count || '').replace('[count]', state.heldCount).replace('[total]', state.total));
      state.segments.forEach(function(filled) {
        segments.appendChild(el('span', 'cart-drawer__anniv-set-segment' +
          (filled ? ' cart-drawer__anniv-set-segment--filled' : '')));
      });
      this.container.appendChild(segments);

      var key = theme.AnnivSetLogic.messageKey(state, soldOut);
      if (key !== 'complete') {
        this.container.appendChild(el('p', 'cart-drawer__anniv-set-message', strings[key]));
      }

      if (state.complete) {
        if (!soldOut) this.paintGift();
        this.missing = [];
        return;
      }

      this.missing = state.missing;
      this.index = 0;
      this.paintCarousel();
    },

    // 3 of 3: a confirmation row, deliberately NOT a cart line (no price, no
    // checkout effect). Same image column as a cart line / upsell card.
    paintGift: function() {
      var row = el('div', 'cart-drawer__anniv-set-gift');
      if (config.magazine && config.magazine.image) {
        var img = el('img', 'cart-drawer__anniv-set-gift-image');
        img.src = config.magazine.image;
        img.alt = config.magazine.title || '';
        img.loading = 'lazy';
        row.appendChild(img);
      }
      var info = el('div', 'cart-drawer__anniv-set-gift-info');
      info.appendChild(el('div', 'cart-drawer__anniv-set-gift-name', strings.complete_title));
      info.appendChild(el('div', 'cart-drawer__anniv-set-gift-line', strings.complete_line));
      info.appendChild(el('div', 'cart-drawer__anniv-set-gift-note', strings.stock_note));
      row.appendChild(info);
      this.container.appendChild(row);
    },

    // Missing pieces as the drawer's standard single-card carousel: chevrons
    // only when more than one piece is missing (spec section 6 ruling).
    paintCarousel: function() {
      if (!this.missing.length) return;
      var carousel = el('div', 'cart-drawer__upsell-carousel cart-drawer__anniv-set-carousel');
      var multi = this.missing.length > 1;

      if (multi) carousel.appendChild(this.arrow(-1));
      this.cardEl = el('div', 'cart-drawer__upsell-card');
      carousel.appendChild(this.cardEl);
      if (multi) carousel.appendChild(this.arrow(1));

      this.container.appendChild(carousel);
      this.renderCard();
    },

    arrow: function(direction) {
      var btn = el('button', 'cart-drawer__upsell-arrow cart-drawer__upsell-arrow--' + (direction > 0 ? 'next' : 'prev'));
      btn.type = 'button';
      btn.innerHTML = direction > 0 ? '&rsaquo;' : '&lsaquo;';
      btn.setAttribute('aria-label', direction > 0 ?
        theme.strings.cartDrawerUpsellArrowNext : theme.strings.cartDrawerUpsellArrowPrevious);
      btn.addEventListener('click', function() {
        var length = this.missing.length;
        this.index = direction > 0 ?
          theme.CartUpsellCarousel.nextIndex(this.index, length) :
          theme.CartUpsellCarousel.previousIndex(this.index, length);
        this.renderCard();
      }.bind(this));
      return btn;
    },

    renderCard: function() {
      var item = this.missing[this.index];
      if (!item || !this.cardEl) return;
      this.cardEl.style.transition = 'none';
      this.cardEl.classList.remove('cart-drawer__upsell-card--visible');
      this.cardEl.innerHTML = '';
      theme.CartUpsellCard.build(this.cardEl, item, function(select, evt) {
        theme.CartUpsellCard.add(select && select.value, evt.currentTarget, { openDrawer: !this.isPage });
      }.bind(this));
      this.cardEl.offsetHeight; // force reflow, same enter-fade technique as CartUpsell
      this.cardEl.style.transition = '';
      this.cardEl.classList.add('cart-drawer__upsell-card--visible');
    }
  };

  var modules = Array.prototype.map.call(document.querySelectorAll('[data-anniv-set]'), function(container) {
    return new AnnivSet(container);
  });
  if (!modules.length) return;

  var pending = null;
  function refreshAll() {
    // Coalesce bursts (cart:updated + cart:build + ajaxProduct:added for one
    // write) into a single cart.js read per module.
    if (pending) return;
    pending = setTimeout(function() {
      pending = null;
      modules.forEach(function(module) { module.refresh(); });
    }, 50);
  }

  document.addEventListener('cart:build', refreshAll);
  document.addEventListener('cart:updated', refreshAll);
  document.addEventListener('ajaxProduct:added', refreshAll);
  refreshAll();
})();
